import { useState } from 'react';
import {
  Search, SlidersHorizontal, Plus, Coins, Pencil, ScrollText, ChevronRight,
  Building2, Archive, AlertTriangle, X
} from 'lucide-react';
import './WholesalerMobileHome.css';

// iOS uygulamasının WebView'da açtığı Toptancılar ekranının (/mobile/toptancilar) ana görünümü.
// Hesaplama yapmaz: özet, kurlar ve toptancı grupları WholesalerPayments'ın kendi değerleridir
// (web ile birebir aynı rakamlar). Modallar (detay, borç/ödeme, arşiv…) WholesalerPayments'tan gelir.

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GOLD', 'SILVER'];
const CURRENCY_LABELS = { TRY: 'Türk Lirası', USD: 'Dolar', EUR: 'Euro', GOLD: 'Has Altın', SILVER: 'Has Gümüş' };
const PERIODS = [
  { id: 'current', label: 'Güncel' },
  { id: 'all', label: 'Tüm Zamanlar' },
  { id: 'thisMonth', label: 'Bu Ay' },
  { id: 'lastMonth', label: 'Geçen Ay' },
  { id: 'thisYear', label: 'Bu Yıl' },
  { id: 'custom', label: 'Tarih Aralığı' }
];
const SORTS = [
  { id: 'debt_desc', label: 'Borç ↓' },
  { id: 'debt_asc', label: 'Borç ↑' },
  { id: 'name_asc', label: 'A→Z' },
  { id: 'name_desc', label: 'Z→A' }
];

const fmt = (v) => Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtTL = (v) => `₺${fmt(v)}`;
const fmtTL0 = (v) => `₺${Number(v || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
// Kartlarda kısa birim: "650,50 gr altın" (web'deki "gr Has Altın" telefonda satırı taşırıyor)
const SHORT_UNIT = { TRY: ['₺', ''], USD: ['$', ''], EUR: ['€', ''], GOLD: ['', ' gr altın'], SILVER: ['', ' gr gümüş'] };
const fmtShort = (v, curr) => {
  const [pre, post] = SHORT_UNIT[curr] || SHORT_UNIT.TRY;
  return `${pre}${fmt(v)}${post}`;
};
const isZero = (v) => Math.abs(v || 0) < 0.0001;

const WholesalerMobileHome = ({
  summaryPeriod, setSummaryPeriod,
  summaryCustomFrom, setSummaryCustomFrom, summaryCustomTo, setSummaryCustomTo,
  summaryStats, liveRates, currencyInfo, toTRY,
  searchTerm, setSearchTerm,
  filterDebtStatus, setFilterDebtStatus, filterCurrencyType, setFilterCurrencyType, filterSortBy, setFilterSortBy,
  wholesalerGroups,
  onOpenSummaryDetail, onOpenDetail, onEditWholesaler, onNewPayment, onNewWholesaler, onOpenArchive,
  onOtherGroup
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = [filterDebtStatus !== 'all', filterCurrencyType !== 'all', filterSortBy !== 'debt_desc'].filter(Boolean).length;

  const netTRY = summaryStats.totalDebtTRY - summaryStats.totalCreditTRY;
  const netKind = isZero(netTRY) ? 'zero' : netTRY > 0 ? 'debt' : 'credit';
  const currencyRows = CURRENCIES
    .map(curr => ({ curr, d: summaryStats.byCurrency[curr] || { total: 0, paid: 0, remaining: 0, credit: 0, overdue: 0 } }))
    .filter(({ d }) => d.total || d.credit);

  return (
    <div className="wmh">
      {/* Net durum kartı */}
      <section className={`wmh-hero is-${netKind}`}>
        <div className="wmh-hero-label">
          {netKind === 'credit' ? 'Net Alacak' : netKind === 'debt' ? 'Net Borç' : 'Net Durum'}
          <span className="wmh-hero-sub"> · anlık satış kuruyla ₺</span>
        </div>
        <div className="wmh-hero-value">{isZero(netTRY) ? '₺0,00' : fmtTL(Math.abs(netTRY))}</div>
        <div className="wmh-hero-stats">
          <button type="button" className="wmh-stat" onClick={() => summaryStats.totalDebtTRY > 0 && onOpenSummaryDetail('debt')}>
            <span className="wmh-stat-label">Kalan Borç</span>
            <span className="wmh-stat-value is-debt">{fmtTL0(summaryStats.totalDebtTRY)}</span>
          </button>
          <button type="button" className="wmh-stat" onClick={() => summaryStats.totalCreditTRY > 0 && onOpenSummaryDetail('credit')}>
            <span className="wmh-stat-label">Alacak</span>
            <span className="wmh-stat-value is-credit">{fmtTL0(summaryStats.totalCreditTRY)}</span>
          </button>
          <div className="wmh-stat">
            <span className="wmh-stat-label">Ödenen</span>
            <span className="wmh-stat-value">{fmtTL0(summaryStats.totalPaidTRY)}</span>
          </div>
        </div>
        {summaryStats.totalOverdueTRY > 0 && (
          <div className="wmh-hero-alert">
            <AlertTriangle size={14} />
            <span>{summaryStats.overdueCount} gecikmiş kayıt · {fmtTL(summaryStats.totalOverdueTRY)}</span>
          </div>
        )}
      </section>

      {/* Dönem */}
      <div className="wmh-chips" role="tablist" aria-label="Dönem">
        {PERIODS.map(p => (
          <button key={p.id} type="button" role="tab" aria-selected={summaryPeriod === p.id}
            className={`wmh-chip${summaryPeriod === p.id ? ' is-active' : ''}`}
            onClick={() => setSummaryPeriod(p.id)}>{p.label}</button>
        ))}
      </div>
      {summaryPeriod === 'custom' && (
        <div className="wmh-range">
          <input type="date" value={summaryCustomFrom} onChange={e => setSummaryCustomFrom(e.target.value)} aria-label="Başlangıç" />
          <span>→</span>
          <input type="date" value={summaryCustomTo} onChange={e => setSummaryCustomTo(e.target.value)} aria-label="Bitiş" />
          {(summaryCustomFrom || summaryCustomTo) && (
            <button type="button" className="wmh-range-clear" onClick={() => { setSummaryCustomFrom(''); setSummaryCustomTo(''); }} aria-label="Temizle"><X size={14} /></button>
          )}
        </div>
      )}

      {/* Birim bazında özet */}
      <div className="wmh-section-title">Birim Bazında</div>
      {currencyRows.length === 0 ? (
        <div className="wmh-card wmh-muted-card">Bu dönemde kayıt yok</div>
      ) : (
        <div className="wmh-card wmh-currencies">
          {currencyRows.map(({ curr, d }) => {
            const net = (d.remaining || 0) - (d.credit || 0);
            const kind = isZero(net) ? 'zero' : net > 0 ? 'debt' : 'credit';
            const rate = liveRates[curr];
            return (
              <div key={curr} className={`wmh-cur cur-${curr}`}>
                <div className="wmh-cur-head">
                  <span className="wmh-cur-badge">{currencyInfo[curr]?.icon}</span>
                  <span className="wmh-cur-name">{CURRENCY_LABELS[curr]}</span>
                  <span className={`wmh-cur-net is-${kind}`}>
                    {kind === 'zero' ? '—' : fmtShort(Math.abs(net), curr)}
                    {kind !== 'zero' && <small>{kind === 'debt' ? 'borç' : 'alacak'}</small>}
                  </span>
                </div>
                <div className="wmh-cur-meta">
                  {d.total > 0 && <span>Toplam {fmt(d.total)}</span>}
                  {d.paid > 0 && <span className="is-credit">Ödenen {fmt(d.paid)}</span>}
                  {d.overdue > 0 && <span className="is-overdue">Gecikmiş {fmt(d.overdue)}</span>}
                  {curr !== 'TRY' && rate > 0 && kind !== 'zero' && <span className="wmh-cur-tl">≈ {fmtTL(Math.abs(net) * rate)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hızlı işlemler */}
      <div className="wmh-actions">
        <button type="button" className="wmh-action is-primary" onClick={onNewPayment}><Coins size={18} /><span>Yeni Ödeme</span></button>
        <button type="button" className="wmh-action" onClick={onNewWholesaler}><Building2 size={18} /><span>Yeni Toptancı</span></button>
        <button type="button" className="wmh-action" onClick={onOpenArchive}><Archive size={18} /><span>Arşiv</span></button>
      </div>

      {/* Arama + filtre */}
      <div className="wmh-search-row">
        <label className="wmh-search">
          <Search size={17} />
          <input type="search" placeholder="Toptancı veya ödeme ara" value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} enterKeyHint="search" />
          {searchTerm && <button type="button" onClick={() => setSearchTerm('')} aria-label="Aramayı temizle"><X size={15} /></button>}
        </label>
        <button type="button" className={`wmh-filter-btn${showFilters || activeFilterCount ? ' is-active' : ''}`}
          onClick={() => setShowFilters(v => !v)} aria-expanded={showFilters} aria-label="Filtre">
          <SlidersHorizontal size={18} />
          {activeFilterCount > 0 && <span className="wmh-filter-count">{activeFilterCount}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="wmh-card wmh-filters">
          <div className="wmh-filter-group">
            <span className="wmh-filter-label">Durum</span>
            <div className="wmh-seg">
              {[{ id: 'all', label: 'Tümü' }, { id: 'hasDebt', label: 'Borçlu' }, { id: 'noDebt', label: 'Temiz' }].map(o => (
                <button key={o.id} type="button" className={filterDebtStatus === o.id ? 'is-active' : ''} onClick={() => setFilterDebtStatus(o.id)}>{o.label}</button>
              ))}
            </div>
          </div>
          <div className="wmh-filter-group">
            <span className="wmh-filter-label">Birim</span>
            <div className="wmh-mini-chips">
              <button type="button" className={filterCurrencyType === 'all' ? 'is-active' : ''} onClick={() => setFilterCurrencyType('all')}>Tümü</button>
              {CURRENCIES.map(c => (
                <button key={c} type="button" className={filterCurrencyType === c ? 'is-active' : ''} onClick={() => setFilterCurrencyType(c)}>{currencyInfo[c]?.icon} {c === 'GOLD' ? 'Altın' : c === 'SILVER' ? 'Gümüş' : c}</button>
              ))}
            </div>
          </div>
          <div className="wmh-filter-group">
            <span className="wmh-filter-label">Sırala</span>
            <div className="wmh-seg">
              {SORTS.map(o => (
                <button key={o.id} type="button" className={filterSortBy === o.id ? 'is-active' : ''} onClick={() => setFilterSortBy(o.id)}>{o.label}</button>
              ))}
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button type="button" className="wmh-filter-reset" onClick={() => { setFilterDebtStatus('all'); setFilterCurrencyType('all'); setFilterSortBy('debt_desc'); }}>Filtreleri temizle</button>
          )}
        </div>
      )}

      {/* Toptancı listesi */}
      <div className="wmh-section-title">
        Toptancılar <span className="wmh-count">{wholesalerGroups.length}</span>
      </div>

      {wholesalerGroups.length === 0 ? (
        <div className="wmh-card wmh-empty">
          <Building2 size={34} />
          <p>{searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz toptancı yok'}</p>
          {!searchTerm && <button type="button" className="wmh-empty-btn" onClick={onNewWholesaler}><Plus size={15} /> Toptancı Ekle</button>}
        </div>
      ) : (
        <div className="wmh-list">
          {wholesalerGroups.map(group => {
            const isOther = !group.wholesaler_id;
            const initials = group.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const firstUnpaid = group.payments.find(p => (p.status === 'pending' || p.status === 'partial') && p.payment_type !== 'income');
            const balances = Object.entries(group.totalByCurrency).filter(([, data]) => !isZero(data.net));
            const paidParts = Object.entries(group.totalByCurrency).filter(([, data]) => data.paid > 0);
            const open = (tab, curr) => (isOther ? onOtherGroup(tab, firstUnpaid) : onOpenDetail(group.wholesaler_id, tab, curr));

            return (
              <article key={group.key} className="wmh-ws">
                <button type="button" className="wmh-ws-head" onClick={() => open('history')}>
                  <span className={`wmh-avatar${isOther ? ' is-other' : ''}`}>{initials}</span>
                  <span className="wmh-ws-info">
                    <span className="wmh-ws-name">{group.name}</span>
                    <span className="wmh-ws-sub">
                      {group.company_name && group.company_name !== group.name ? `${group.company_name} · ` : ''}
                      {group.payments.length === 0 ? 'Kayıt yok' : `${group.payments.length} kayıt`}
                      {group.pendingCount > 0 && ` · ${group.pendingCount} açık`}
                    </span>
                  </span>
                  <ChevronRight size={18} className="wmh-ws-chevron" />
                </button>

                {balances.length > 0 ? (
                  <div className="wmh-balances">
                    {balances.map(([curr, data]) => {
                      const credit = data.net < 0;
                      const amount = Math.abs(data.net);
                      const tl = toTRY(amount, curr);
                      return (
                        <button key={curr} type="button" className={`wmh-bal cur-${curr} ${credit ? 'is-credit' : 'is-debt'}`}
                          onClick={() => open(credit ? 'history' : 'payDebt', curr)}>
                          <span className="wmh-bal-amount">{fmtShort(amount, curr)}</span>
                          <span className="wmh-bal-kind">{credit ? 'alacak' : 'verecek'}{tl !== null ? ` · ₺${fmt(tl)}` : ''}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="wmh-clean">Hesap temiz{paidParts.length > 0 ? ` · ${paidParts.map(([c, d]) => fmtShort(d.paid, c)).join(', ')} ödendi` : ''}</div>
                )}

                <div className="wmh-ws-actions">
                  <button type="button" className="is-debt-add" onClick={() => (isOther ? onNewPayment() : onOpenDetail(group.wholesaler_id, 'addDebt'))}><Plus size={15} />Borç</button>
                  {!isOther && <button type="button" className="is-credit-add" onClick={() => onOpenDetail(group.wholesaler_id, 'addCredit')}><Plus size={15} />Alacak</button>}
                  <button type="button" className="is-pay" disabled={!firstUnpaid} onClick={() => firstUnpaid && open('payDebt')}><Coins size={15} />Öde</button>
                  <button type="button" onClick={() => open('history')}><ScrollText size={15} />Geçmiş</button>
                  {!isOther && <button type="button" className="is-icon" onClick={() => onEditWholesaler(group.wholesaler)} aria-label="Düzenle"><Pencil size={15} /></button>}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WholesalerMobileHome;
